import React from 'react';
import { Box, Chip, TableCell, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { RxBasketMembership } from './rxRowActions';

/**
 * PRX-137 — the small cells and badges the Prescriptions tab added. They
 * live here so the 1,500-line table only wires them in.
 *
 * Every reason is a `describeChild` tooltip on a focusable element, the same
 * pattern as `RxStatusCell`, so a keyboard user reaches it and a screen
 * reader keeps the element's own name.
 */

export const FILLWARE_NO_DUE_REASON =
  'No due date: the fill date or the days supply is missing in Fillware, or the Rx is deleted, transferred out or inactive.';

export const BY_REQUEST_ONLY_NO_DUE_REASON = 'No due date: this Rx is by request only.';

/**
 * EVERY subscription basket line of the Rx is by request only (`isPrn`), so
 * nothing schedules it. One scheduled line is enough for the Rx to keep its
 * due date. False for an Rx on no line, and while the basket is loading or
 * could not be read: unknown is not "by request only".
 */
export function isByRequestOnly(membership: RxBasketMembership): boolean {
  return membership.status === 'ready' && membership.lines.length > 0 && membership.lines.every((line) => line.isPrn);
}

/**
 * Days until the Rx is due: fill date + days supply, from today (0 = due
 * today, negative = overdue). Kroll sends `DueDays`. For Fillware the
 * server computes it the same way (PRX-137) and sends null when an input is
 * missing.
 *
 * PRX-151 (PRX-137 Q4): on Fillware a by-request-only Rx has no next due
 * date, so the cell shows none whatever the fill date and days supply give.
 * Kroll is left as it was.
 */
export const DueDaysCell: React.FC<{
  dueDays: number | null | undefined;
  databaseType: string | null;
  byRequestOnly?: boolean;
}> = ({ dueDays, databaseType, byRequestOnly = false }) => {
  if (byRequestOnly && databaseType === 'fillware') {
    return (
      <TableCell>
        <Tooltip describeChild title={BY_REQUEST_ONLY_NO_DUE_REASON}>
          <span tabIndex={0} data-testid="rx-due-by-request-only">
            —
          </span>
        </Tooltip>
      </TableCell>
    );
  }
  if (dueDays == null) {
    return (
      <TableCell>
        {databaseType === 'fillware' ? (
          <Tooltip describeChild title={FILLWARE_NO_DUE_REASON}>
            <span tabIndex={0} data-testid="rx-due-missing">
              —
            </span>
          </Tooltip>
        ) : (
          '—'
        )}
      </TableCell>
    );
  }
  return (
    <TableCell>
      <Chip
        label={dueDays}
        size="small"
        color={dueDays <= 0 ? 'error' : dueDays <= 7 ? 'warning' : 'default'}
        variant="outlined"
      />
    </TableCell>
  );
};

/**
 * "Request only" when the Rx's subscription basket line is by request only
 * (`isPrn`). Empty when the Rx is in no basket or its line is scheduled. A
 * failed basket read says so and never reads as empty.
 */
export const PrnCell: React.FC<{ membership: RxBasketMembership }> = ({ membership }) => {
  if (membership.status === 'error') {
    return (
      <TableCell>
        <Tooltip describeChild title="Couldn't check the subscription">
          <span tabIndex={0} data-testid="rx-prn-unknown">
            ?
          </span>
        </Tooltip>
      </TableCell>
    );
  }
  const on = membership.status === 'ready' && membership.lines.some((line) => line.isPrn);
  return <TableCell>{on ? <Chip label="Request only" size="small" variant="outlined" color="info" /> : null}</TableCell>;
};

const normaliseName = (name: string | null | undefined): string =>
  String(name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/** The patient's specialty-drug names, normalised for matching. */
export function specialtyDrugNames(records: ReadonlyArray<{ drugName: string | null }> | undefined): ReadonlySet<string> {
  return new Set((records ?? []).map((r) => normaliseName(r.drugName)).filter(Boolean));
}

/**
 * A specialty-drug record names a drug, not an Rx. The row matches when the
 * name it shows (brand, else generic: what "Mark as specialty" posts) equals a
 * recorded name, ignoring case and spacing.
 */
export function isSpecialtyRx(
  rx: { BrandName?: string | null; GenericName?: string | null },
  names: ReadonlySet<string>,
): boolean {
  const shown = normaliseName(rx.BrandName || rx.GenericName);
  return !!shown && names.has(shown);
}

export const SpecialtyBadge: React.FC = () => (
  <Chip label="Specialty" size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
);

/** The row is involved in an interaction found by the last "Check interactions". */
export const InteractionFlag: React.FC<{ reasons: string[] }> = ({ reasons }) => (
  <Tooltip
    describeChild
    title={
      <Box component="span" sx={{ display: 'block' }}>
        {reasons.map((reason) => (
          <Box component="span" key={reason} sx={{ display: 'block' }}>
            {reason}
          </Box>
        ))}
      </Box>
    }
  >
    <Box
      component="span"
      tabIndex={0}
      role="img"
      aria-label="Interaction"
      data-testid="rx-interaction-flag"
      sx={{ display: 'inline-flex', color: 'warning.main' }}
    >
      <WarningAmberIcon sx={{ fontSize: '1rem' }} aria-hidden="true" />
    </Box>
  </Tooltip>
);
